import { useEffect, useRef, useState } from 'react';
import '../../styles/inputs/iconSelectInput.scss';
import CustomSVGs from '../customSVGs/CustomSVGs';
import { arrowHeadRightLightSVG, tickSVG } from '../../lib/allCustomSVGs';

type ItemProps = {
  icon?: any;
  text: string;
  value: string;
};

interface Props {
  filterOptions: ItemProps[];
  onFilterChange?: (selectedValue: string) => void;
  value: string;
  title?: string;
  marginTop?: string;
  scrollWhenOpen?: boolean; // ✅ Optional control prop
  hideTick?: boolean;
  readonly?: boolean; // ✅ Optional prop to make input read-only
}

const IconSelectInput = ({
  filterOptions,
  hideTick,
  onFilterChange,
  readonly,
  value,
  title,
  marginTop = '0',
  scrollWhenOpen = false, // ✅ Default false if not passed
}: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null); // ✅ Root element to scroll into view

  const matchedOption = filterOptions.find((option) => option.value === value);
  const selectedFilter = matchedOption || {
    text: '-- Select --',
    value: '',
    icon: null,
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // ✅ Scroll into view when dropdown opens (if enabled)
  useEffect(() => {
    if (dropdownOpen && scrollWhenOpen && rootRef.current) {
      rootRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [dropdownOpen, scrollWhenOpen]);

  const handleFilterSelect = (option: ItemProps) => {
    if (option.value !== value) {
      onFilterChange?.(option.value);
    }
    setDropdownOpen(false);
  };

  return (
    <div
      ref={rootRef} // ✅ Attach ref for scrolling
      style={{ marginTop }}
      className='iconSelectInput'
    >
      {title && <div className='iconSelectInput-title'>{title}</div>}

      <div
        className='iconSelectInput__filter'
        onClick={() => {
          if (readonly) return; // Prevent interaction if read-only
          setDropdownOpen((prev) => !prev);
        }}
        data-is-open={dropdownOpen ? 'true' : 'false'}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (readonly) return; // Prevent interaction if read-only
            setDropdownOpen((prev) => !prev);
          }
        }}
      >
        {selectedFilter.icon && (
          <CustomSVGs
            className='iconSelectInput__filter--icon'
            svg={selectedFilter.icon}
          />
        )}
        <div className='iconSelectInput__filter--text'>
          {selectedFilter.text}
        </div>
        <CustomSVGs
          className='iconSelectInput__filter--arrow'
          svg={arrowHeadRightLightSVG}
        />
      </div>
      <div className='iconSelectInput__dropdown--container' ref={dropdownRef}>
        {dropdownOpen && (
          <div className='iconSelectInput__dropdown'>
            {filterOptions.map((option) => {
              const isSelected = value === option.value;
              return (
                <div
                  key={option.value}
                  className={`iconSelectInput__dropdown-item ${
                    isSelected ? 'selected' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterSelect(option);
                  }}
                >
                  <div
                    style={{
                      opacity: isSelected ? '1' : '0',
                      display: hideTick ? 'none' : 'block',
                    }}
                    className='iconSelectInput__filter--tick'
                  >
                    <CustomSVGs svg={tickSVG} />
                  </div>
                  {option.icon && <CustomSVGs svg={option.icon} />}
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IconSelectInput;
